import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useLanguage } from '../../context/LanguageContext';
import { marcoApi } from './api';
import { ASSETS } from '../../config/assets';
import {
  Plus, FileText, Calendar, User, Lock, Trash2, LogOut, ChevronRight, Shield, Download, CopyPlus
} from 'lucide-react';
import { Button } from '../../components/ui/button';

const MarcoDashboard = () => {
  const { user, logout } = useAuth();
  const { language } = useLanguage();
  const navigate = useNavigate();
  const [marcos, setMarcos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleteId, setDeleteId] = useState(null);
  const [downloadingId, setDownloadingId] = useState(null);

  const t = (en, fr) => language === 'fr' ? fr : en;

  useEffect(() => {
    loadMarcos();
  }, []);

  const loadMarcos = async () => {
    try {
      const data = await marcoApi.list();
      setMarcos(data);
    } catch (err) {
      console.error('Failed to load marcos:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      await marcoApi.delete(id);
      setMarcos(prev => prev.filter(m => m.id !== id));
      setDeleteId(null);
    } catch (err) {
      console.error('Delete failed:', err);
    }
  };

  const handleDownload = async (marco) => {
    setDownloadingId(marco.id);
    try {
      const API = '';
      const res = await fetch(`${API}/api/marcom/${marco.id}/pdf?lang=${language}`, {
        credentials: 'include',
      });
      if (!res.ok) throw new Error('Download failed');
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${marco.name}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Download failed:', err);
    } finally {
      setDownloadingId(null);
    }
  };

  const handleDuplicate = (marco) => {
    navigate('/marcom/create', { state: { duplicateFrom: marco } });
  };

  const handleLogout = async () => {
    await logout();
    navigate('/portal');
  };

  const formatDate = (iso) => {
    const d = new Date(iso);
    return d.toLocaleDateString(language === 'fr' ? 'fr-CA' : 'en-CA', {
      year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit'
    });
  };

  return (
    <div data-testid="marco-dashboard" className="min-h-screen bg-[#0D0D0D]">
      {/* Top Bar */}
      <header className="border-b border-white/10 bg-[#0D0D0D]/95 backdrop-blur-md sticky top-0 z-50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/">
              <img src={ASSETS.LOGO} alt="YUL On Tap" className="h-10 w-auto" />
            </Link>
            <div className="h-6 w-px bg-white/10" />
            <span className="font-heading text-lg font-semibold text-white tracking-wide">Marcom</span>
          </div>
          <div className="flex items-center gap-3">
            {user?.role === 'super_admin' && (
              <Link to="/marcom/admin">
                <Button variant="ghost" size="sm" className="text-white/60 hover:text-white hover:bg-white/5" data-testid="admin-panel-btn">
                  <Shield className="w-4 h-4 mr-1.5" strokeWidth={1.5} />
                  Admin
                </Button>
              </Link>
            )}
            <span className="text-white/50 text-sm hidden sm:block">{user?.name}</span>
            <button
              onClick={handleLogout}
              data-testid="logout-btn"
              className="p-2 text-white/40 hover:text-white hover:bg-white/5 rounded-lg transition-colors"
            >
              <LogOut className="w-4 h-4" strokeWidth={1.5} />
            </button>
          </div>
        </div>
      </header>

      {/* Sub-nav */}
      <div className="border-b border-white/5">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 flex items-center gap-1">
          <div className="px-4 py-3 text-sm text-white font-medium border-b-2 border-wine">
            {t('My Marcoms', 'Mes Marcoms')}
          </div>
          <Link to="/marcom/library" className="px-4 py-3 text-sm text-white/40 hover:text-white/60 transition-colors">
            {t('Library', 'Bibliotheque')}
          </Link>
        </div>
      </div>

      {/* Main Content */}
      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-8 sm:py-12">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
          <div>
            <h1 className="font-heading text-3xl sm:text-4xl font-semibold text-white">
              {t('My Marcoms', 'Mes Marcoms')}
            </h1>
            <p className="text-white/50 mt-1">
              {t('Create and manage marketing presentations', 'Creez et gerez vos presentations marketing')}
            </p>
          </div>
          <Link to="/marcom/create">
            <Button
              data-testid="create-marco-btn"
              className="bg-wine hover:bg-wine-light text-white rounded-xl px-6 h-12 text-base font-medium"
            >
              <Plus className="w-5 h-5 mr-2" strokeWidth={1.5} />
              {t('Create Marcom', 'Creer un Marcom')}
            </Button>
          </Link>
        </div>

        {/* Marco List */}
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="w-8 h-8 border-2 border-wine border-t-transparent rounded-full animate-spin" />
          </div>
        ) : marcos.length === 0 ? (
          <div data-testid="empty-state" className="text-center py-20">
            <div className="w-20 h-20 bg-white/5 rounded-2xl flex items-center justify-center mx-auto mb-6">
              <FileText className="w-10 h-10 text-white/20" strokeWidth={1.5} />
            </div>
            <h2 className="font-heading text-xl text-white/70 mb-2">
              {t('No Marcoms yet', 'Aucun Marcomm pour le moment')}
            </h2>
            <p className="text-white/40 mb-6">
              {t('Create your first marketing presentation in seconds.', 'Creez votre premiere presentation marketing en quelques secondes.')}
            </p>
            <Link to="/marcom/create">
              <Button className="bg-wine hover:bg-wine-light text-white rounded-xl px-6 h-11">
                <Plus className="w-4 h-4 mr-2" strokeWidth={1.5} />
                {t('Create Marcom', 'Creer un Marcom')}
              </Button>
            </Link>
          </div>
        ) : (
          <div className="grid gap-4">
            {marcos.map((marco) => (
              <div
                key={marco.id}
                data-testid={`marco-card-${marco.id}`}
                className="group bg-[#1A1A1A] border border-white/10 rounded-xl p-5 hover:border-wine/30 transition-all duration-200"
              >
                <div className="flex items-center gap-4">
                  {/* Icon */}
                  <div className="w-12 h-12 bg-wine/10 rounded-xl flex items-center justify-center flex-shrink-0">
                    <FileText className="w-6 h-6 text-wine" strokeWidth={1.5} />
                  </div>

                  {/* Info */}
                  <div className="flex-1 min-w-0">
                    <h3 className="font-heading text-lg font-medium text-white truncate">{marco.name}</h3>
                    <div className="flex flex-wrap items-center gap-3 mt-1 text-sm text-white/40">
                      <span className="flex items-center gap-1">
                        <FileText className="w-3.5 h-3.5" strokeWidth={1.5} />
                        {marco.page_count} {t('pages', 'pages')}
                      </span>
                      <span className="flex items-center gap-1">
                        <Calendar className="w-3.5 h-3.5" strokeWidth={1.5} />
                        {formatDate(marco.created_at)}
                      </span>
                      <span className="flex items-center gap-1">
                        <User className="w-3.5 h-3.5" strokeWidth={1.5} />
                        {marco.created_by_name}
                      </span>
                    </div>
                  </div>

                  {/* Password pill */}
                  <div className="hidden sm:flex items-center gap-1.5 px-3 py-1.5 bg-white/5 rounded-lg border border-white/10">
                    <Lock className="w-3.5 h-3.5 text-wine" strokeWidth={1.5} />
                    <span className="text-xs text-white/50 font-mono">{marco.password}</span>
                  </div>

                  {/* Actions */}
                  <div className="flex items-center gap-1">
                    {deleteId === marco.id ? (
                      <div className="flex items-center gap-2">
                        <Button
                          size="sm"
                          onClick={() => handleDelete(marco.id)}
                          data-testid={`confirm-delete-${marco.id}`}
                          className="bg-red-600 hover:bg-red-700 text-white text-xs h-8 rounded-lg"
                        >
                          {t('Delete', 'Supprimer')}
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => setDeleteId(null)}
                          className="text-white/50 hover:text-white text-xs h-8"
                        >
                          {t('Cancel', 'Annuler')}
                        </Button>
                      </div>
                    ) : (
                      <>
                        <button
                          onClick={() => handleDownload(marco)}
                          disabled={downloadingId === marco.id}
                          data-testid={`download-marco-${marco.id}`}
                          className="p-2 text-white/20 hover:text-wine rounded-lg transition-colors"
                        >
                          {downloadingId === marco.id ? (
                            <div className="w-4 h-4 border-2 border-wine border-t-transparent rounded-full animate-spin" />
                          ) : (
                            <Download className="w-4 h-4" strokeWidth={1.5} />
                          )}
                        </button>
                        <button
                          onClick={() => handleDuplicate(marco)}
                          data-testid={`duplicate-marco-${marco.id}`}
                          title={t('Duplicate', 'Dupliquer')}
                          className="p-2 text-white/20 hover:text-wine rounded-lg transition-colors"
                        >
                          <CopyPlus className="w-4 h-4" strokeWidth={1.5} />
                        </button>
                        <button
                          onClick={() => setDeleteId(marco.id)}
                          data-testid={`delete-marco-${marco.id}`}
                          className="p-2 text-white/20 hover:text-red-400 rounded-lg transition-colors"
                        >
                          <Trash2 className="w-4 h-4" strokeWidth={1.5} />
                        </button>
                        <Link to={`/marco/view/${marco.id}`}>
                          <button className="p-2 text-white/20 hover:text-wine rounded-lg transition-colors">
                            <ChevronRight className="w-4 h-4" strokeWidth={1.5} />
                          </button>
                        </Link>
                      </>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
};

export default MarcoDashboard;
